import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useTimer } from 'react-timer-hook'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Button, Input, PinCode, Toast, useInput, usePinCode, useViewport } from '@chilecompra/react-kit/components'
import { useLanguage } from '@chilecompra/react-kit/i18n'
import {
  checkInputErrorEmail,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  EMAIL_CHECK
} from '@chilecompra/react-kit/utils'
import { isLocalEnv } from '../../config/settings/environment'
import {
  onRegisterCodeValidationThunk,
  onResendRegistrationEmailThunk,
  onResetRegisterWizardState,
  onSendRegistrationEmailThunk,
  onUpdateRegistrationInformationThunk
} from './RegisterWizard.actions'
import {
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  Label,
  MobileNavigation,
  Root,
  Section,
  Title
} from './RegisterWizardEmailVerify.styles'

/**
 * The RegisterWizardEmailVerify' container.
 */
const RegisterWizardEmailVerify = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { emailSent, errorSendingEmail, codeIsValid, sendingEmail } = useSelector(
    state => state.registerWizard.emailVerify
  )
  const { language } = useLanguage()
  const { error, guest, loading } = useSelector(state => state.registerWizard)
  const { size } = useViewport()
  const { seconds, minutes, isRunning, restart } = useTimer({ expiryTimestamp: new Date() })
  const {
    error: emailError,
    value: emailValue,
    onChange: handleEmailChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardEmailVerify__fieldRequiredError')),
      checkInputErrorEmail(language('RegisterWizardEmailVerify__fieldFormatError')),
      checkInputErrorMaxLength(80, language('RegisterWizardEmailVerify__fieldMaxLengthError'))
    ],
    initialValue: isLocalEnv ? 'oscar.osses@chilecompra.cl' : ''
  })
  const { value: codeValue, onChange: handleCodeChange } = usePinCode()
  const remainingTime = useMemo(() => {
    return `${minutes}:${seconds >= 10 ? seconds.toString()[0] : '0'}${
      seconds >= 10 ? seconds.toString()[1] : seconds.toString()
    }`
  }, [minutes, seconds])
  const confTimer = () => {
    const time = new Date()
    time.setMinutes(time.getMinutes() + 3)
    restart(time)
  }
  const handleSendingCode = () => {
    if (emailSent) {
      batch(async () => {
        await dispatch(onUpdateRegistrationInformationThunk({ email: emailValue }))
        await dispatch(onResendRegistrationEmailThunk({ callback: () => confTimer() }))
      })
      return
    }

    dispatch(
      onSendRegistrationEmailThunk({
        email: emailValue,
        callback: () => confTimer()
      })
    )
  }
  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }
  const handleNext = useCallback(() => {
    if (isLocalEnv) {
      dispatch(onRegisterCodeValidationThunk({ code: codeValue.join('') }))
      onChangeStep(guest.isForeign ? 'password-generation' : 'business-info')
      return
    }

    dispatch(onRegisterCodeValidationThunk({ code: codeValue.join('') }))
  }, [dispatch, isLocalEnv, onRegisterCodeValidationThunk, codeValue, guest])

  const canNotNextButtonClick = useCallback(() => {
    const hadErrors = emailError !== ''
    const hadInvalidValues = emailValue === ''
    const hadInvalidCodeValues = codeValue.length !== 4

    return hadErrors || hadInvalidValues || hadInvalidCodeValues || loading
  }, [codeValue, emailValue, loading])
  const canNotSendingCode = useCallback(() => {
    const hadErrors = emailError !== ''
    const hadInvalidValues = emailValue === ''
    const hadErrorEmail = !EMAIL_CHECK.test(emailValue)

    return hadErrors || hadInvalidValues || hadErrorEmail || isRunning || sendingEmail
  }, [emailError, emailValue, isRunning, sendingEmail])
  const disabledNextButton = canNotNextButtonClick()
  const disabledSendingCode = canNotSendingCode()

  useEffect(() => {
    if (!error && !loading && codeIsValid) {
      onChangeStep(guest.isForeign ? 'password-generation' : 'business-info')
    }
  }, [error, loading, codeIsValid])

  useAnalyticsPageView('/paso/2/correo')

  return (
    <Root>
      <Section container>
        <Section item lg={7} maxWidth="546px" xs={12}>
          <Title>{language('RegisterWizardEmailVerify__title')}</Title>
          <Instructions>{language('RegisterWizardEmailVerify__description')}</Instructions>
          {error && !loading && errorSendingEmail && (
            <Toast
              margin="20px 0"
              severity="warning"
              width="100%"
              title={language('RegisterWizardEmailVerify__sendingCodeError_title')}
            >
              <br />
              {language('RegisterWizardEmailVerify__sendingCodeError')}
            </Toast>
          )}
          {error && !loading && codeIsValid !== undefined && !codeIsValid && (
            <Toast
              margin="20px 0"
              severity="warning"
              width="100%"
              title={language('RegisterWizardEmailVerify__invalidCodeError_title')}
            >
              <br />
              {language('RegisterWizardEmailVerify__invalidCodeError')}
            </Toast>
          )}
          <Section container margin="20px 0 0" spacing={1}>
            <Section item maxWidth={size.isUpToMedium && '100%'} md={7} xs={12}>
              <Input
                autoFocus
                required
                error={emailError}
                label="Email"
                placeholder={language('RegisterWizardEmailVerify__emailInputPlaceholder')}
                value={emailValue}
                onChange={handleEmailChange}
              />
            </Section>
            <Section
              item
              margin={size.isDownToSmall ? '18px 0 0' : '27.5px 0 0'}
              md={5}
              padding={size.isDownToMedium && '0 0 0 14px'}
              xs={12}
            >
              <Button
                color={!emailSent ? 'default' : 'success'}
                disabled={disabledSendingCode}
                loading={sendingEmail}
                variant={!emailSent || !isRunning ? 'contained' : 'outlined'}
                width="100%"
                onClick={handleSendingCode}
              >
                {!emailSent || !isRunning
                  ? language('RegisterWizardEmailVerify__emailButtonSend')
                  : language('RegisterWizardEmailVerify__emailButtonSent')}
              </Button>
            </Section>
          </Section>
          {!loading && emailSent && isRunning && (
            <Section container margin={size.isUpToMedium ? '0 0 15px' : '14px 0 0'} padding="4px" spacing={1}>
              <Label>
                {`${language('RegisterWizardEmailVerify__codeRemainingTime__1')} ${remainingTime} ${language(
                  'RegisterWizardEmailVerify__codeRemainingTime__2'
                )}`}
              </Label>
            </Section>
          )}
          {!loading && emailSent && (
            <Section container padding="4px" spacing={1}>
              <PinCode
                required
                label={language('RegisterWizardEmailVerify__codeInputLabel')}
                fieldsLength={4}
                value={codeValue}
                onChange={handleCodeChange}
              />
            </Section>
          )}
        </Section>
        <Hidden mdDown>
          <Section item margin="-35px 0 0" padding="0 0 0 51px">
            <Image alt={language('RegisterWizardEmailVerify__title')} src="/assets/img/steps/paso3.svg" />
          </Section>
        </Hidden>
      </Section>
      {!size.isTiny && (
        <Section container margin="52px 0 0" maxWidth="568px">
          <DesktopNavigation>
            <Button onClick={handleBack} type="button" variant="text">
              {language('RegisterWizardEmailVerify__backButton')}
            </Button>
            {emailSent && (
              <Button
                color="primary"
                disabled={disabledNextButton}
                loading={loading}
                margin="0 0 0 30px"
                type="button"
                onClick={handleNext}
              >
                {language('RegisterWizardEmailVerify__nextButton')}
              </Button>
            )}
          </DesktopNavigation>
        </Section>
      )}
      {size.isTiny && (
        <MobileNavigation>
          <Button onClick={handleBack} type="button" variant="text" width="168px">
            {language('RegisterWizardEmailVerify__backButton')}
          </Button>
          {emailSent && (
            <Button
              color="primary"
              disabled={disabledNextButton}
              loading={loading}
              type="button"
              width="168px"
              onClick={handleNext}
            >
              {language('RegisterWizardEmailVerify__nextButton')}
            </Button>
          )}
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardEmailVerify.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardEmailVerify.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardEmailVerify
